const ZERO_DECIMAL_CURRENCIES = new Set([
  "BIF", "CLP", "DJF", "GNF", "JPY", "KMF", "KRW", "MGA", "PYG",
  "RWF", "UGX", "VND", "VUV", "XAF", "XOF", "XPF"
]);

const THREE_DECIMAL_CURRENCIES = new Set([
  "BHD", "IQD", "JOD", "KWD", "LYD", "OMR", "TND"
]);

export function currencyExponent(currency) {
  const code = String(currency || "EUR").toUpperCase();
  if (ZERO_DECIMAL_CURRENCIES.has(code)) return 0;
  if (THREE_DECIMAL_CURRENCIES.has(code)) return 3;
  return 2;
}

export function toMinorUnits(value, currency = "EUR") {
  const numeric = typeof value === "number"
    ? value
    : Number(String(value ?? "").trim().replace(",", "."));

  if (!Number.isFinite(numeric)) return null;
  return Math.round(numeric * (10 ** currencyExponent(currency)));
}

function nonBlank(value) {
  const normalized = String(value ?? "").trim();
  return normalized ? normalized : null;
}

export function normalizeDataLayerContents(items, currency = "EUR") {
  if (!Array.isArray(items)) return [];

  return items.slice(0, 100).map((item) => {
    const content = {
      id: nonBlank(item?.item_id ?? item?.variant_id ?? item?.id),
      name: nonBlank(item?.item_name ?? item?.product_title ?? item?.name),
      content_type: "product",
      quantity: Number.isInteger(Number(item?.quantity))
        ? Math.max(0, Number(item.quantity))
        : 1
    };

    const amount = toMinorUnits(item?.price, currency);
    if (amount !== null) {
      content.amount = amount;
      content.currency = String(currency).toUpperCase();
    }

    return Object.fromEntries(
      Object.entries(content).filter(([, value]) => value !== null)
    );
  });
}

export function mapViewEntry(entry, fallbackCurrency = "EUR") {
  if (!entry || !["view_item", "view_item_list"].includes(entry.event)) {
    return null;
  }

  const currency = String(entry.currency || fallbackCurrency).toUpperCase();
  const data = {
    type: "contents",
    contents: normalizeDataLayerContents(entry.items, currency)
  };
  const amount = toMinorUnits(entry.value, currency);
  if (amount !== null) {
    data.amount = amount;
    data.currency = currency;
  }

  return { eventName: "contents_viewed", data };
}

function aggregateQuantities(items) {
  const quantities = new Map();
  for (const item of items || []) {
    const id = nonBlank(item?.variant_id ?? item?.item_id ?? item?.id);
    if (!id) continue;
    quantities.set(id, (quantities.get(id) || 0) + Math.max(0, Number(item.quantity) || 0));
  }
  return quantities;
}

export function diffCartItems(previousItems, currentItems) {
  const previous = aggregateQuantities(previousItems);
  const current = aggregateQuantities(currentItems);
  const byId = new Map();

  for (const item of currentItems || []) {
    const id = nonBlank(item?.variant_id ?? item?.item_id ?? item?.id);
    if (id && !byId.has(id)) byId.set(id, item);
  }

  const added = [];
  for (const [id, quantity] of current) {
    const delta = quantity - (previous.get(id) || 0);
    if (delta > 0) added.push({ ...byId.get(id), quantity: delta });
  }
  return added;
}

function cartFromDataLayerEntry(entry, currency) {
  return {
    currency,
    total_price: toMinorUnits(entry.value, currency),
    items: (entry.items || []).map((item) => ({
      variant_id: item.item_id,
      product_id: item.item_parent_id,
      product_title: item.item_name,
      final_price: toMinorUnits(item.price, currency),
      quantity: Number(item.quantity) || 0
    }))
  };
}

function contentsFromCartItems(items, currency) {
  return (items || []).slice(0, 100).map((item) => {
    const content = {
      id: nonBlank(item.variant_id ?? item.item_id ?? item.id),
      name: nonBlank(item.product_title ?? item.item_name ?? item.title),
      content_type: "product",
      quantity: Math.max(0, Number(item.quantity) || 0)
    };
    const amount = Number.isInteger(item.final_price)
      ? item.final_price
      : toMinorUnits(item.price, currency);
    if (Number.isInteger(amount)) {
      content.amount = amount;
      content.currency = currency;
    }
    return Object.fromEntries(Object.entries(content).filter(([, value]) => value !== null));
  });
}

function createEventId(prefix, cryptoObject = globalThis.crypto) {
  const suffix = cryptoObject?.randomUUID?.()
    || `${Date.now()}_${Math.random().toString(36).slice(2)}`;
  return `elamo_${prefix}_${suffix}`;
}

function readCookie(documentObject, name) {
  const prefix = `${name}=`;
  for (const part of String(documentObject.cookie || "").split(";")) {
    const trimmed = part.trim();
    if (!trimmed.startsWith(prefix)) continue;
    try {
      return decodeURIComponent(trimmed.slice(prefix.length));
    } catch {
      return trimmed.slice(prefix.length);
    }
  }
  return "";
}

function consentFromCustomerPrivacy(customerPrivacy) {
  if (!customerPrivacy) return null;
  try {
    return Boolean(
      customerPrivacy.analyticsProcessingAllowed()
      && customerPrivacy.marketingAllowed()
    );
  } catch {
    return null;
  }
}

function consentFromPandectesEntry(entry) {
  if (entry?.event !== "Pandectes_Consent_Update") return null;
  const categories = entry.pandectes_categories || {};
  return categories.C0002 === "allow" && categories.C0003 === "allow";
}

export function startOpenAIAdsIntegration(windowObject, documentObject, config) {
  if (!windowObject || !documentObject || !config?.pixelId || windowObject.__elamoOpenAIAdsStarted) {
    return null;
  }
  windowObject.__elamoOpenAIAdsStarted = true;

  const debug = (...args) => {
    if (config.debug) console.debug("[ElaMo OpenAI Ads]", ...args);
  };

  const currency = String(config.currency || "EUR").toUpperCase();
  let consentKnown = false;
  let consentAllowed = false;
  let pageMeasured = false;
  let latestViewEntry = null;
  let lastCart = null;
  let lastCartAttributeFingerprint = "";
  let checkoutPrepared = false;
  const seenViews = new Set();

  const routesRoot = windowObject.Shopify?.routes?.root || "/";
  const cartUrl = `${routesRoot}cart.js`;
  const cartUpdateUrl = `${routesRoot}cart/update.js`;

  const seedCart = Promise.resolve()
    .then(() => windowObject.fetch(cartUrl, { credentials: "same-origin" }))
    .then((response) => response.ok ? response.json() : null)
    .then((cart) => {
      if (cart) lastCart = cart;
      return cart;
    })
    .catch(() => null);

  function queuePixel(...args) {
    if (typeof windowObject.oaiq !== "function") return false;
    try {
      windowObject.oaiq(...args);
      return true;
    } catch (error) {
      debug("pixel call failed", args[0], error);
      return false;
    }
  }

  function measure(eventName, data, eventId = createEventId(eventName, windowObject.crypto)) {
    if (!consentAllowed) return false;
    if (!queuePixel("measure", eventName, data, { event_id: eventId })) return false;
    debug("measured", eventName);
    return true;
  }

  function persistPrivateCartAttributes(attributes, force = false) {
    const fingerprint = JSON.stringify(attributes);
    if (!force && fingerprint === lastCartAttributeFingerprint) return;
    lastCartAttributeFingerprint = fingerprint;

    try {
      Promise.resolve(windowObject.fetch(cartUpdateUrl, {
        method: "POST",
        credentials: "same-origin",
        keepalive: true,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ attributes })
      })).catch(() => {
        lastCartAttributeFingerprint = "";
      });
    } catch {
      lastCartAttributeFingerprint = "";
    }
  }

  function syncAttributionToCart(force = false) {
    if (!consentAllowed) return;
    const oppref = readCookie(documentObject, "__oppref");
    const obref = readCookie(documentObject, "__obref");
    persistPrivateCartAttributes({
      __openai_ads_consent: "granted",
      __openai_ads_oppref: oppref,
      __openai_ads_obref: obref
    }, force);
  }

  function clearAttributionFromCart() {
    persistPrivateCartAttributes({
      __openai_ads_consent: "denied",
      __openai_ads_oppref: "",
      __openai_ads_obref: ""
    });
  }

  function measureLatestView() {
    if (!latestViewEntry) return;
    const mapped = mapViewEntry(latestViewEntry, currency);
    if (!mapped) return;
    const ids = mapped.data.contents.map((item) => item.id || item.group_id || "").join(",");
    const fingerprint = `${latestViewEntry.event}:${windowObject.location.pathname}:${ids}`;
    if (seenViews.has(fingerprint)) return;
    seenViews.add(fingerprint);
    measure(mapped.eventName, mapped.data);
  }

  function applyConsent(allowed, source) {
    const next = Boolean(allowed);
    const previous = consentAllowed;
    if (consentKnown && next === previous) return;
    consentKnown = true;
    consentAllowed = next;
    queuePixel("consent", next);
    debug("consent", next, source);

    if (!next) {
      if (previous) clearAttributionFromCart();
      return;
    }

    if (!pageMeasured) {
      pageMeasured = measure("page_viewed", { type: "contents" });
    }
    measureLatestView();
    syncAttributionToCart();
    windowObject.setTimeout(syncAttributionToCart, 750);
    windowObject.setTimeout(syncAttributionToCart, 2500);
  }

  async function handleAddToCart(entry) {
    await seedCart;
    const eventCurrency = String(entry.currency || lastCart?.currency || currency).toUpperCase();
    const currentCart = cartFromDataLayerEntry(entry, eventCurrency);
    if (!lastCart) {
      lastCart = currentCart;
      debug("items_added skipped because no baseline cart was available");
      return;
    }

    const added = diffCartItems(lastCart.items, currentCart.items);
    lastCart = currentCart;
    if (!consentAllowed || added.length === 0) return;

    const contents = contentsFromCartItems(added, eventCurrency);
    const amount = contents.reduce(
      (sum, item) => sum + ((item.amount || 0) * (item.quantity || 0)),
      0
    );
    measure("items_added", {
      type: "contents",
      amount,
      currency: eventCurrency,
      contents
    });
    // Adding to an empty/completed cart can rotate Shopify's cart token. Force
    // the attributes onto the newly active cart even when their values match
    // the previous cart's fingerprint.
    syncAttributionToCart(true);
  }

  function handleDataLayerEntry(entry) {
    const pandectesConsent = consentFromPandectesEntry(entry);
    if (pandectesConsent !== null) applyConsent(pandectesConsent, "pandectes_data_layer");

    if (["view_item", "view_item_list"].includes(entry?.event)) {
      latestViewEntry = entry;
      if (consentAllowed) measureLatestView();
    } else if (entry?.event === "add_to_cart") {
      void handleAddToCart(entry).catch(() => {
        debug("add-to-cart processing failed");
      });
    }
  }

  function safelyHandleDataLayerEntry(entry) {
    try {
      handleDataLayerEntry(entry);
    } catch (error) {
      debug("data-layer processing failed", error);
    }
  }

  const dataLayer = Array.isArray(windowObject.dataLayer)
    ? windowObject.dataLayer
    : (windowObject.dataLayer = []);
  dataLayer.forEach(safelyHandleDataLayerEntry);
  const originalPush = dataLayer.push;
  dataLayer.push = function (...entries) {
    const result = originalPush.apply(this, entries);
    entries.forEach(safelyHandleDataLayerEntry);
    return result;
  };

  documentObject.addEventListener("visitorConsentCollected", (event) => {
    const apiConsent = consentFromCustomerPrivacy(windowObject.Shopify?.customerPrivacy);
    if (apiConsent !== null) {
      applyConsent(apiConsent, "shopify_customer_privacy");
      return;
    }
    const detail = event?.detail || {};
    const analytics = detail.analyticsProcessingAllowed ?? detail.analytics;
    const marketing = detail.marketingAllowed ?? detail.marketing;
    if (typeof analytics === "boolean" && typeof marketing === "boolean") {
      applyConsent(analytics && marketing, "shopify_consent_event");
    }
  });

  let privacyLoadAttempted = false;
  let privacyPolls = 0;
  const privacyTimer = windowObject.setInterval(() => {
    const customerPrivacy = windowObject.Shopify?.customerPrivacy;
    const current = consentFromCustomerPrivacy(customerPrivacy);
    if (current !== null) {
      applyConsent(current, "shopify_customer_privacy_initial");
      windowObject.clearInterval(privacyTimer);
      return;
    }

    if (!privacyLoadAttempted && typeof windowObject.Shopify?.loadFeatures === "function") {
      privacyLoadAttempted = true;
      windowObject.Shopify.loadFeatures(
        [{ name: "consent-tracking-api", version: "0.1" }],
        () => {
          const loaded = consentFromCustomerPrivacy(windowObject.Shopify?.customerPrivacy);
          if (loaded !== null) applyConsent(loaded, "shopify_customer_privacy_loaded");
        }
      );
    }

    privacyPolls += 1;
    if (privacyPolls >= 60) {
      windowObject.clearInterval(privacyTimer);
      debug("Customer Privacy API unavailable; integration remains fail-closed");
    }
  }, 250);

  function isCheckoutElement(element) {
    if (!(element instanceof windowObject.Element)) return false;
    const target = element.closest("a,button,input");
    if (!target) return false;
    const selectors = config.checkoutSelectors || [];
    if (selectors.some((selector) => {
      try { return target.matches(selector); } catch { return false; }
    })) return true;
    const href = target.getAttribute("href") || "";
    const name = target.getAttribute("name") || "";
    return /\/checkouts?\b|shop\.app\/checkout/i.test(href) || name === "checkout";
  }

  function prepareCheckoutAttribution() {
    if (checkoutPrepared || !consentAllowed) return;
    checkoutPrepared = true;
    // Shopify's checkout_started Customer Event is the authoritative browser
    // boundary. The theme only performs a final non-blocking attribution sync
    // before navigation so it never delays or intercepts checkout.
    syncAttributionToCart(true);
  }

  documentObject.addEventListener("click", (event) => {
    if (isCheckoutElement(event.target)) prepareCheckoutAttribution();
  }, true);

  documentObject.addEventListener("submit", (event) => {
    if (isCheckoutElement(event.submitter)) prepareCheckoutAttribution();
  }, true);

  return {
    applyConsent,
    handleDataLayerEntry,
    getState: () => ({ consentKnown, consentAllowed, pageMeasured, checkoutPrepared })
  };
}

if (typeof window !== "undefined" && typeof document !== "undefined") {
  startOpenAIAdsIntegration(window, document, window.__elamoOpenAIAdsConfig || {});
}
